/**
 * FaceTracker — stable per-face IDs across frames for the Joint
 * Attention pipeline (Phase 2-B).
 *
 * Why this exists separately from useMultiFaceLandmarker:
 *   - The hook previously used a *greedy* IoU match (first raw face takes
 *     its highest-IoU prev track; second face must pick from what is
 *     left). For 2-face dyads with crossing motion, this swaps IDs.
 *   - The hook also dropped lost tracks the very next frame, so a single
 *     occluded frame ended a track and made the dyadic metrics (JAI/MGI)
 *     restart with a new partner.
 *
 * Two fixes here:
 *   1. **Optimal small-N assignment** — enumerate every assignment of
 *      raw faces to prev tracks (N ≤ 4 → at most 24 permutations) and
 *      pick the one with the highest summed IoU. For dyadic JA (N=2)
 *      this is the safe choice.
 *   2. **Lost-track memory** — a prev track that does not match this
 *      frame is *retained* (not deleted) for up to `lostTimeoutMs`. If
 *      the face reappears within that window with IoU above
 *      `reacquireIoU`, it inherits the original ID.
 *
 * Output IDs are stable strings (`face_0`, `face_1`, …) emitted in the
 * same order as the input bboxes.
 */

export interface TBBox {
  x: number; y: number; w: number; h: number;
}

interface Track {
  id: string;
  bbox: TBBox;
  lastSeen: number;        // ms timestamp
  firstSeen: number;       // ms timestamp
  age: number;             // frames the track has accumulated
  hitFrames: number;       // frames the track was matched this session
}

export interface TrackerSnapshot {
  /** Stable ID per input bbox, in input order. */
  ids: string[];
  /** Whether each ID was already known (true) or freshly minted (false). */
  isNew: boolean[];
}

/** Optional tuning constants for testing / future calibration. */
export interface FaceTrackerOptions {
  /** Min IoU to consider a (raw, track) pair a match. Default 0.20. */
  matchIoU?: number;
  /** Min IoU to *re-acquire* a lost track. Default 0.10 (looser — the
   *  face may have shifted while gone). */
  reacquireIoU?: number;
  /** ms after which an un-seen track is forgotten. Default 1500. */
  lostTimeoutMs?: number;
}

export function bboxIoU(a: TBBox, b: TBBox): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a.w * a.h + b.w * b.h - inter;
  return union > 0 ? inter / union : 0;
}

/** Enumerate all assignments (permutations) of cols to a subset of rows
 *  of equal size, returning [colIdx → rowIdx | -1] vectors. -1 means
 *  the col is unassigned. For our N ≤ 4 use-case this is fast (≤ 64). */
function enumerateAssignments(nCols: number, nRows: number): number[][] {
  // We'll generate every injection from {0..nCols-1} to {0..nRows-1} ∪ {-1}.
  // For each col we can either pick an unused row or leave it -1.
  const results: number[][] = [];
  const cur: number[] = new Array(nCols).fill(-1);
  const usedRow = new Array(nRows).fill(false);
  const recurse = (col: number) => {
    if (col === nCols) { results.push(cur.slice()); return; }
    // Option 1: leave this col unassigned
    cur[col] = -1;
    recurse(col + 1);
    // Option 2: assign one of the unused rows
    for (let r = 0; r < nRows; r++) {
      if (usedRow[r]) continue;
      cur[col] = r;
      usedRow[r] = true;
      recurse(col + 1);
      usedRow[r] = false;
    }
    cur[col] = -1;
  };
  recurse(0);
  return results;
}

export class FaceTracker {
  private tracks: Track[] = [];
  private nextId = 0;
  private readonly matchIoU: number;
  private readonly reacquireIoU: number;
  private readonly lostTimeoutMs: number;

  constructor(opts: FaceTrackerOptions = {}) {
    this.matchIoU = opts.matchIoU ?? 0.20;
    this.reacquireIoU = opts.reacquireIoU ?? 0.10;
    this.lostTimeoutMs = opts.lostTimeoutMs ?? 1500;
  }

  reset(): void {
    this.tracks = [];
    this.nextId = 0;
  }

  /** Returns the list of currently known tracks (active + lost-but-still
   *  -retained). Useful for tests and debugging. */
  inspect(): ReadonlyArray<Track> {
    return this.tracks;
  }

  update(rawBboxes: TBBox[], now: number): TrackerSnapshot {
    // 1. Prune tracks that have been unseen too long.
    this.tracks = this.tracks.filter(t => now - t.lastSeen <= this.lostTimeoutMs);

    const nCols = rawBboxes.length;
    const nRows = this.tracks.length;

    // 2. Build IoU matrix.
    const ious: number[][] = Array.from({ length: nCols }, () =>
      new Array(nRows).fill(0));
    for (let c = 0; c < nCols; c++) {
      for (let r = 0; r < nRows; r++) {
        ious[c][r] = bboxIoU(rawBboxes[c], this.tracks[r].bbox);
      }
    }

    // 3. Pick the assignment maximising sum-IoU.
    //    A (col,row) pair only counts if its IoU clears the threshold.
    //    For active tracks (last seen this frame) use matchIoU; for lost
    //    tracks use the looser reacquireIoU.
    const thresholdFor = (r: number) =>
      now - this.tracks[r].lastSeen < 50 ? this.matchIoU : this.reacquireIoU;
    let bestScore = -Infinity;
    let bestAssign: number[] = new Array(nCols).fill(-1);
    if (nRows > 0 && nCols > 0) {
      for (const assign of enumerateAssignments(nCols, nRows)) {
        let score = 0;
        let ok = true;
        for (let c = 0; c < nCols; c++) {
          const r = assign[c];
          if (r < 0) continue;
          if (ious[c][r] < thresholdFor(r)) { ok = false; break; }
          score += ious[c][r];
        }
        if (!ok) continue;
        if (score > bestScore) { bestScore = score; bestAssign = assign; }
      }
    }

    // 4. Emit IDs in input order, minting new tracks for unassigned cols.
    const ids: string[] = new Array(nCols);
    const isNew: boolean[] = new Array(nCols);
    for (let c = 0; c < nCols; c++) {
      const r = bestAssign[c];
      if (r >= 0) {
        const t = this.tracks[r];
        t.bbox = rawBboxes[c];
        t.lastSeen = now;
        t.age += 1;
        t.hitFrames += 1;
        ids[c] = t.id;
        isNew[c] = false;
      } else {
        const id = `face_${this.nextId++}`;
        this.tracks.push({
          id, bbox: rawBboxes[c], lastSeen: now, firstSeen: now,
          age: 1, hitFrames: 1,
        });
        ids[c] = id;
        isNew[c] = true;
      }
    }
    return { ids, isNew };
  }
}
